import React from "react";
import { AlertCircle } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useSeo } from "@/lib/api";
import { Section, Reveal, Eyebrow } from "@/components/common/Section";
import { CtaTrio } from "@/components/common/CtaButtons";
import Breadcrumb from "@/components/common/Breadcrumb";
import FinalCta from "@/components/common/FinalCta";

export default function Frontaliers() {
  const { t, lang } = useLang();
  const p = t.frontaliersPage;
  useSeo({ lang, path: t.nav.frontaliers.path, title: `${t.nav.frontaliers.label} — NEKASSUR`, description: p.intro });

  return (
    <>
      <section className="bg-nek-navy">
        <div className="nek-container py-12 sm:py-16">
          <Breadcrumb onDark items={[{ label: t.nav.frontaliers.label }]} />
          <Reveal className="mt-8 max-w-3xl">
            <Eyebrow className="!text-nek-teal-100">{p.eyebrow}</Eyebrow>
            <h1 className="nek-h1 mt-4 text-white" style={{ fontSize: "clamp(2.2rem,4vw,3.4rem)" }}>{p.title}</h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-white/70">{p.intro}</p>
            <div className="mt-8"><CtaTrio variant="dark" /></div>
          </Reveal>
        </div>
      </section>

      <Section tone="white">
        <Reveal className="flex items-start gap-3 rounded-xl border border-nek-border bg-nek-light p-5">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-nek-blue" />
          <p className="text-sm text-nek-gray">{p.disclaimer}</p>
        </Reveal>
        <Reveal className="mt-12"><h2 className="nek-h2" style={{ fontSize: "clamp(1.7rem,2.6vw,2.4rem)" }}>{p.blocksTitle}</h2></Reveal>
        <div className="mt-8 grid gap-5 sm:grid-cols-2">
          {p.blocks.map((b) => (
            <Reveal key={b.title} className="nek-card p-6">
              <span className="h-0.5 w-8 rounded-full bg-nek-teal" aria-hidden="true" />
              <h3 className="mt-5 text-lg font-heading font-bold text-nek-navy">{b.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-nek-gray">{b.text}</p>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Comparaison Suisse vs Luxembourg */}
      <Section tone="light">
        <Reveal className="max-w-2xl">
          <h2 className="nek-h2" style={{ fontSize: "clamp(1.7rem,2.6vw,2.4rem)" }}>{p.comparison.title}</h2>
          <p className="nek-intro mt-4">{p.comparison.intro}</p>
        </Reveal>

        <Reveal className="mt-8 overflow-hidden rounded-2xl border border-nek-border bg-white" data-testid="frontaliers-comparison">
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-nek-navy text-left text-white">
                  <th scope="col" className="w-[18%] px-5 py-4 font-heading">{p.comparison.topicLabel}</th>
                  <th scope="col" className="w-[41%] px-5 py-4 font-heading">{p.comparison.suisseLabel}</th>
                  <th scope="col" className="w-[41%] px-5 py-4 font-heading">{p.comparison.luxLabel}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-nek-border">
                {p.comparison.rows.map((row, i) => (
                  <tr key={row.topic} className={i % 2 ? "bg-nek-light" : "bg-white"}>
                    <th scope="row" className="px-5 py-4 text-left align-top font-heading font-bold text-nek-navy">{row.topic}</th>
                    <td className="px-5 py-4 align-top leading-relaxed text-nek-ink">{row.suisse}</td>
                    <td className="px-5 py-4 align-top leading-relaxed text-nek-ink">{row.lux}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile : cartes empilées */}
          <div className="divide-y divide-nek-border md:hidden">
            {p.comparison.rows.map((row, i) => (
              <div key={row.topic} className="p-5">
                <p className="font-heading font-bold text-nek-navy">{row.topic}</p>
                <div className="mt-3 rounded-lg bg-nek-light p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-nek-blue">{p.comparison.suisseLabel}</p>
                  <p className="mt-1 text-sm leading-relaxed text-nek-ink">{row.suisse}</p>
                </div>
                <div className="mt-2 rounded-lg bg-nek-light p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-nek-blue">{p.comparison.luxLabel}</p>
                  <p className="mt-1 text-sm leading-relaxed text-nek-ink">{row.lux}</p>
                </div>
              </div>
            ))}
          </div>
        </Reveal>

        <p className="mt-6 flex items-start gap-2 text-xs italic text-nek-gray">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-nek-gray" />
          {p.comparison.note}
        </p>
      </Section>

      <FinalCta />
    </>
  );
}
